import { useEffect, useLayoutEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { FriendlyError } from '@/components/FriendlyError';
import { captureAppException } from '@/lib/analytics/posthog-errors';
import {
  attemptStaleChunkReload,
  hasStaleChunkReloadBeenAttempted,
  isStaleChunkLoadError,
} from '@/lib/stale-chunk-recovery';

import { NotFoundPage } from './NotFoundPage';

/**
 * Replaces `app/error.tsx` and `app/global-error.tsx`. React Router hands the
 * thrown error here; a 404 thrown by the router renders the same not-found
 * page an unmatched path would.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;
  const isStaleChunk = !isNotFound && isStaleChunkLoadError(error);
  const recoveringStaleChunk = isStaleChunk && !hasStaleChunkReloadBeenAttempted();

  useLayoutEffect(() => {
    if (!recoveringStaleChunk) return;
    attemptStaleChunkReload();
  }, [recoveringStaleChunk]);

  useEffect(() => {
    if (isNotFound || recoveringStaleChunk) return;
    captureAppException(error);
  }, [error, isNotFound, recoveringStaleChunk]);

  if (isNotFound) return <NotFoundPage />;
  if (recoveringStaleChunk) return null;

  return (
    <FriendlyError
      eyebrow="Support"
      description="We've logged the error and will take a look. You can try again, open your current trip, or head back home."
      actions={[
        { label: 'Try again', onClick: () => window.location.reload(), variant: 'primary' },
        { label: 'Go home', href: '/' },
        { label: 'Current trip', href: '/trips/active' },
      ]}
    />
  );
}
